import { Button, Flex, Typography, Select, Card, Tour, Tooltip } from 'antd'
import DurationPicker from '../../common/DurationPicker'
import { CloseOutlined, CloseCircleFilled } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/pro-duotone-svg-icons'
import * as routeAddModel from './routeAddModel'
import { useReducer, useState, useEffect } from 'react'
import { useQueries } from 'react-query'
import { pricingTypeName } from '../../../common/constants'
import { getWorkareaGroups } from '../../../domain/workareas/groupsApi'
import type { WorkareaGroupWithWorkareas } from '../../../domain/workareas/group'
import type { Workarea } from '../../../domain/job'
import { RouteJob } from '../routingApi'

export type Props = {
  selectedJobs: routeAddModel.Job[]
  onBack: () => void
  onClose: () => void
  onSelectedJobsChange: (jobs: routeAddModel.Job[]) => void
  onSave: (state: RouteJob[]) => void
  existingJobs?: RouteJob[]
  editJobId?: number | null
  isOpen: boolean
}

const { Text } = Typography

const AddJobsWorkareas = ({ selectedJobs, onBack, onClose, onSelectedJobsChange, onSave, existingJobs, editJobId, isOpen }: Props) => {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(editJobId?.toString() || null)
  const viewed = window.localStorage.getItem('add-jobs-workareas-tour-viewed') === 'true'
  const [isTourOpen, setIsTourOpen] = useState(!viewed)

  const [state, dispatch] = useReducer(routeAddModel.reducer, routeAddModel.loadRouteJobs(selectedJobs, existingJobs))

  const selectedJobIndex = selectedJobs.findIndex(job => job.jobId.toString() === selectedJobId)
  const isFirstJob = selectedJobIndex === 0
  const isLastJob = selectedJobIndex === selectedJobs.length - 1


  const workareaGroupsResults = useQueries(
    selectedJobs.map(job => ({
      queryKey: ['workareaGroups', { jobId: job.jobId }],
      queryFn: () => getWorkareaGroups(job.jobId),
      enabled: !!job.jobId,
      refetchOnWindowFocus: false,
    }))
  )

  const allWorkareas: Workarea[] = workareaGroupsResults
    .map(result => result.data as WorkareaGroupWithWorkareas[] | undefined)
    .filter(Boolean)
    .flatMap(groups => groups!.flatMap(group => group.workareas))

  const workareaGroupsQuery = workareaGroupsResults[selectedJobIndex] || { data: [] }

  const selectedJobWorkareas = workareaGroupsQuery.data
    ? workareaGroupsQuery.data.flatMap((group: WorkareaGroupWithWorkareas) => group.workareas)
    : []

  const handleNext = () => {
    if (!isLastJob) {
      setSelectedJobId(selectedJobs[selectedJobIndex + 1].jobId.toString())
    }
  }

  const handlePrevious = () => {
    if (!isFirstJob) {
      setSelectedJobId(selectedJobs[selectedJobIndex - 1].jobId.toString())
    }
  }

  const handleRemoveJob = (jobId: string) => {
    const updatedJobs = selectedJobs.filter(job => job.jobId.toString() !== jobId)
    dispatch({ type: 'JOB_REMOVED', jobId: parseInt(jobId) })
    onSelectedJobsChange(updatedJobs)
    if (updatedJobs.length === 0) {
      onBack()
    } else if (jobId === selectedJobId) {
      const newIndex = Math.max(0, selectedJobIndex - 1)
      setSelectedJobId(updatedJobs[newIndex].jobId.toString())
    }
  }

  const selectedRouteJob = state.find(job => job.jobId?.toString() === selectedJobId)

  const selectedWorkareaIds = selectedRouteJob?.workAreaIds ? selectedRouteJob.workAreaIds.split(',').filter(Boolean).map(Number) : []

  const assignedWorkareas = allWorkareas.filter(w => selectedWorkareaIds.includes(w.id))

  const assignedWorkareaGroups = assignedWorkareas.map(assignedWorkarea => workareaGroupsQuery.data
    ? workareaGroupsQuery.data.find(group => group.workareas.some(w => w.id === assignedWorkarea.id))
    : null
  )

  useEffect(() => {
    if (!selectedJobId && selectedJobs.length > 0) {
      setSelectedJobId(selectedJobs[0].jobId.toString())
    }
  }, [selectedJobId, selectedJobWorkareas])

  const handleEstimatedDurationChange = (minutes: number) => {
    if (selectedRouteJob) {
      dispatch({ type: 'JOB_ESTIMATED_DURATION_CHANGED', jobId: selectedRouteJob.jobId!, estimatedDurationInMinutes: minutes })
    }
  }

  return <>
    <Flex vertical style={{ height: '100%', position: 'relative' }}>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Add Jobs
          <Tooltip title="Need help? Take a quick tour of the job configuration features"> 
            <Button 
              type="text"
              size="small"
              onClick={() => setIsTourOpen(true)}
              style={{ padding: '4px', marginLeft: '8px' }}
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Button>
          </Tooltip>
        </span>
        <CloseOutlined onClick={onClose} style={{ fontSize: '16px', cursor: 'pointer' }} />
      </Flex>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        padding: '16px', 
        overflow: 'auto', 
        display: 'flex',
        height: 'calc(100% - 65px)' // Subtract footer height
      }}>
        {/* Jobs List */}
        <div id="jobs-list-section" style={{ width: '400px', borderRight: '1px solid #f0f0f0', overflow: 'auto', paddingBottom: '65px' }}>
          {selectedJobs.map((job, index) => {
            const jobRoute = state.find(j => j.jobId === job.jobId)
            const jobWorkarea = jobRoute?.workAreaIds ? jobRoute.workAreaIds.split(',').filter(Boolean).map(Number).map(id => allWorkareas.find(w => w.id === id)).filter(Boolean) : []
            return (
              <Flex
                key={job.jobId.toString()}
                id={index === 0 ? 'first-job-item' : undefined}
                vertical
                onClick={() => setSelectedJobId(job.jobId.toString())}
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  backgroundColor: selectedJobId === job.jobId.toString() ? '#FFCABD' : 'white',
                  position: 'relative'
                }}
              >
                <Flex justify="space-between" align="center" style={{ width: '100%', paddingRight: '32px' }}>
                  <Text style={{ fontSize: '14px', fontWeight: 'bold' }}>{job.jobName}</Text>
                </Flex>
                <Flex justify="space-between" align="center" style={{ width: '100%', paddingRight: '32px', marginTop: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{job.customerDisplayName}</Text>
                </Flex>
                {jobWorkarea.length > 0 && (
                  <Flex justify="space-between" align="center" style={{ width: '100%', paddingRight: '32px', marginTop: '4px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{jobWorkarea.map(w => w?.name).join(', ')}</Text>
                  </Flex>
                )}
                <CloseCircleFilled 
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveJob(job.jobId.toString())
                  }}
                  style={{ 
                    color: '#8c8c8c', 
                    fontSize: '12px',
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer'
                  }} 
                />
              </Flex>
            )
          })}
        </div>

        {/* Work Areas and Schedule Container */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Full Width Header */}
          <Flex justify="space-between" align="center" style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', padding: '0 16px' }}>
            <Flex vertical>
              <Text style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'normal' }}>
                {selectedJobs.find(job => job.jobId.toString() === selectedJobId)?.jobName || 'No Job Selected'}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px', marginBottom: '16px' }}>
                {selectedJobs.find(job => job.jobId.toString() === selectedJobId)?.customerDisplayName || 'No Customer'} | {selectedJobs.find(job => job.jobId.toString() === selectedJobId)?.address1 || 'No Address'}
              </Text>
            </Flex>
            <Flex gap="small">
              {!isFirstJob && (
                <Button 
                  onClick={handlePrevious}
                  style={{ 
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '4px 16px'
                  }}
                >
                  ← Previous
                </Button>
              )}
              {!isLastJob && (
                <Button 
                  id="next-job-button"
                  onClick={handleNext}
                  style={{ 
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '4px 16px'
                  }}
                >
                  Next →
                </Button>
              )}
            </Flex>
          </Flex>

          {/* Content Container */}
          <Flex style={{ flex: 1, flexDirection: 'column', padding: '16px', backgroundColor: '#fafafa' }} gap="middle">
            {/* Schedule Section */}
            <div id="schedule-section" style={{ width: '100%', marginBottom: '16px' }}>
              <Text style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'normal' }}>Schedule</Text>
              <div style={{ 
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #f0f0f0'
              }}>
                <Flex id="duration-picker" align="center" gap="small">
                  <Text type="secondary" style={{ marginRight: 8 }}>Estimated Duration</Text>
                  <DurationPicker
                    value={{ seconds: (selectedRouteJob?.estimatedDurationInMinutes ?? 30) * 60 }}
                    onChange={(d) => handleEstimatedDurationChange(Math.floor(d.seconds / 60))}
                    increment={300}
                    disabled={!selectedRouteJob}
                  />
                </Flex>
              </div>
            </div>

            {/* Work Areas Section */}
            <div id="work-areas-section" style={{ width: '100%' }}>
              <Text style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'normal' }}>Work Areas</Text>
              <div style={{ 
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                height: '90%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div id="work-areas-dropdown" style={{ width: '100%', marginBottom: '16px' }}>
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="Search Work Areas"
                    style={{ width: '100%' }}
                    options={selectedJobWorkareas
                      .filter(workarea => workarea.isApproved)
                      .map(workarea => ({
                        value: workarea.id,
                        label: workarea.name
                      }))}
                    value={selectedWorkareaIds}
                    onChange={(values) => {
                      if (selectedRouteJob) {
                        dispatch({ type: 'JOB_WORKAREAS_CHANGED', jobId: selectedRouteJob.jobId!, workAreaIds: values })
                      }
                    }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    optionFilterProp='label'
                  />
                  <Flex gap="small" style={{ marginTop: '8px' }}>
                    <Button
                      onClick={() => {
                        if (selectedRouteJob) {
                          const allApprovedIds = selectedJobWorkareas
                            .filter(workarea => workarea.isApproved)
                            .map(workarea => workarea.id)
                          dispatch({ type: 'JOB_WORKAREAS_CHANGED', jobId: selectedRouteJob.jobId!, workAreaIds: allApprovedIds })
                        }
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={() => {
                        if (selectedRouteJob) {
                          dispatch({ type: 'JOB_WORKAREAS_CHANGED', jobId: selectedRouteJob.jobId!, workAreaIds: [] })
                        }
                      }}
                    >
                      Unselect All
                    </Button>
                  </Flex>
                </div>
                {assignedWorkareas.length > 0 ? (
                  assignedWorkareas.map((assignedWorkarea, idx) => {
                    const assignedWorkareaGroup = assignedWorkareaGroups[idx]
                    return (
                      <Card key={assignedWorkarea.id} size="small" style={{ width: '100%', marginTop: 4, marginBottom: 4, background: '#fafafa', borderRadius: 8, padding: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <Text style={{ fontSize: 13 }}>{assignedWorkarea.name}</Text>
                          <CloseOutlined style={{ color: '#bfbfbf', fontSize: 12, cursor: 'pointer' }} onClick={() => {
                            if (selectedRouteJob) {
                              const newIds = selectedWorkareaIds.filter(id => id !== assignedWorkarea.id)
                              dispatch({ type: 'JOB_WORKAREAS_CHANGED', jobId: selectedRouteJob.jobId!, workAreaIds: newIds })
                            }
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                          <span>Pricing Type <Text strong style={{ fontSize: 12 }}>{assignedWorkareaGroup?.pricingType !== undefined ? pricingTypeName(assignedWorkareaGroup.pricingType) : 'Unknown'}</Text></span>
                          <span>Approved Visits <Text strong style={{ fontSize: 12 }}>{assignedWorkarea.estimatedOccurrences ?? 0}</Text></span>
                        </div>
                      </Card>
                    )
                  })
                ) : (
                  <Flex 
                    vertical 
                    justify="center" 
                    align="center" 
                    style={{ 
                      flex: 1, 
                      border: '1px dashed #d9d9d9',
                      borderRadius: '8px',
                      padding: '16px',
                      maxHeight: '120px'
                    }}
                  >
                    <Text type="secondary">No Assigned Work Areas</Text>
                  </Flex>
                )}
              </div>
            </div>
          </Flex>
        </div>
      </div>

      {/* Footer with Navigation */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '16px 24px',
        borderTop: '1px solid #f0f0f0',
        backgroundColor: 'white',
        zIndex: 10,
        height: '65px'
      }}>
        <Flex gap="small" justify="space-between">
          <Button id="back-button" type="default" onClick={onBack}>← Back</Button>
          <Flex gap="small" style={{ marginRight: '60px' }}>
            <Button type="default" onClick={onClose}>Cancel</Button>
            <Button id="save-button" type="primary" onClick={() => onSave(state)}>Save</Button>
          </Flex>
        </Flex>
      </div>
    </Flex>
      
      <Tour
        closable={true}
        open={isTourOpen && isOpen}
        onClose={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('add-jobs-workareas-tour-viewed', 'true')
        }}
        onFinish={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('add-jobs-workareas-tour-viewed', 'true')
        }}
        steps={[
          {
            title: 'Select Jobs',
            description: 'Click on any job in the list to configure its work areas and estimated duration. The selected job will be highlighted in orange.',
            target: () => document.getElementById('first-job-item') as HTMLElement || null,
          },
          {
            title: 'Add More Jobs',
            description: 'Use this Back button to return to the job selection screen if you need to add more jobs you might have forgotten.',
            target: () => document.getElementById('back-button') as HTMLElement || null,
          },
          {
            title: 'Navigate Between Jobs',
            description: 'Use the Next button to quickly move between jobs in your list for configuration. This helps you efficiently set up all your jobs.',
            target: () => document.getElementById('next-job-button') as HTMLElement || null,
          },
          {
            title: 'Schedule Settings',
            description: 'Set the estimated duration for each job here. Click on the time picker to adjust how long this job should take. This helps with route planning and scheduling accuracy.',
            target: () => document.getElementById('duration-picker') as HTMLElement || null,
          },
          {
            title: 'Work Areas Selection',
            description: 'Search and select the specific work areas associated with each job. You can select multiple work areas by clicking on them in the dropdown.',
            target: () => document.getElementById('work-areas-dropdown') as HTMLElement || null,
          },
          {
            title: 'Save Route',
            description: 'Once you have configured all jobs with their work areas and durations, click Save to proceed to the route view page to optimize the drive time and schedule the route.',
            target: () => document.getElementById('save-button') as HTMLElement || null,
          },
        ].filter(step => step.target() !== null)}
        type="default"
        disabledInteraction={true}
      />
  </>
}

export default AddJobsWorkareas